import { getChannelAPIPath } from '@treasury/core/http';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';

DashboardService.$inject = ['$resource', '$q', 'TmDi'];

export default function DashboardService($resource, $q, TmDi) {
    return {
        getInformationCenter,
        getBannerAndModalMessages,
        getResources,
        getReportsOfType,
        getPaymentsPendingApproval,

        getAccountGroups,
        createAccountGroup,
        updateGroupName,
        deleteGroup,
        getAccounts,
        addAccounts,
        removeAccount,
        resequenceGroup,
        resequenceAccount,
        getSummaryInfo,
        getTransactionDetail,
        getDepositTransactionDetail,
        getTimeDepositTransactionDetail,
        getLoanTransactionDetail,
        getEntitlements,
        postBatch,
        getConfiguredWidgets,
        setConfiguredWidgets,
        getAvailableWidgets,
        getFavouriteReports,
        getStopRequests,
        postStpBatch,
        getWorkExceptions,
        getAvailableWidgetProducts,
        postArpExceptions,
        postAchExceptions,
        getProductStatus,
        auditUserIteration,
    };

    function postBatch(type, batchPayload) {
        return $resource(
            `${getChannelAPIPath()}dashboard/batch`,
            { type },
            { save: { method: 'POST' } }
        ).save(batchPayload).$promise;
    }

    function postArpExceptions(batchPayload) {
        return $resource(`${getChannelAPIPath()}dashboard/arpExceptionsBatch`).save(batchPayload)
            .$promise;
    }

    function postAchExceptions(batchPayload) {
        return $resource(`${getChannelAPIPath()}dashboard/channelAchExceptionsBatch`).save(
            batchPayload
        ).$promise;
    }

    function getWorkExceptions(type) {
        return $resource(`${getChannelAPIPath()}dashboard/channelWorkExceptions?type=:type`, {
            type,
        }).query().$promise;
    }

    function batchApproveOrReject(batch, action) {
        return $resource(`${getChannelAPIPath()}dashboard/:id/batch?action=:action`, {
            id: 0,
            action,
        }).save(batch).$promise;
    }

    function getEntitlements(accountId) {
        return $resource(`${getChannelAPIPath()}dashboard/entitlements/`).query().$promise;
    }
    function getLoanTransactionDetail(accountId) {
        return $resource(`${getChannelAPIPath()}dashboard/loantransactiondetail/?accountId=:id`, {
            id: accountId,
        }).get().$promise;
    }

    function getTimeDepositTransactionDetail(accountId) {
        return $resource(
            `${getChannelAPIPath()}dashboard/timedeposittransactiondetail/?accountId=:id`,
            {
                id: accountId,
            }
        ).get().$promise;
    }

    function getDepositTransactionDetail(accountId) {
        return $resource(
            `${getChannelAPIPath()}dashboard/depositTransactionDetail/?accountId=:id`,
            {
                id: accountId,
            }
        ).get().$promise;
    }

    function postStpBatch(list) {
        return $resource(`${getChannelAPIPath()}dashboard/stpBatch`, {
            save: { method: 'POST' },
        }).save(list).$promise;
    }

    function getTransactionDetail(accountId, accountType) {
        return $resource(
            `${getChannelAPIPath()}dashboard/transactiondetail/?accountId=:id&accountType=:accountType`,
            { id: accountId, accountType },
            { query: { method: 'GET', isArray: false } }
        ).query().$promise;
    }

    function getSummaryInfo() {
        return $resource(`${getChannelAPIPath()}dashboard/summary`).get().$promise;
    }

    function getAccountGroups() {
        return $resource(`${getChannelAPIPath()}dashboard/myaccounts`).query().$promise;
    }

    function createAccountGroup(group) {
        return $resource(`${getChannelAPIPath()}dashboard/myaccounts/groups`).save(group).$promise;
    }

    function updateGroupName(group) {
        const resource = $resource(
            `${getChannelAPIPath()}dashboard/myaccounts/groups`,
            { groupId: group.id },
            { update: { method: 'PUT' } }
        );
        return resource.update(group).$promise;
    }

    function deleteGroup(groupId) {
        return $resource(`${getChannelAPIPath()}dashboard/myaccounts/groups`, { groupId }).delete()
            .$promise;
    }

    function getAccounts() {
        return $resource(`${getChannelAPIPath()}dashboard/myaccounts/accounts`).query().$promise;
    }

    function addAccounts(accounts, groupId) {
        return $resource(`${getChannelAPIPath()}dashboard/myaccounts/groups/accounts`, {
            groupId,
        }).save(accounts).$promise;
    }

    function removeAccount(groupId, accountId) {
        return $resource(`${getChannelAPIPath()}dashboard/myaccounts/groups/accounts`, {
            groupId,
            accountId,
        }).delete().$promise;
    }

    function resequenceGroup(groupId, newIndex) {
        const resource = $resource(
            `${getChannelAPIPath()}dashboard/myaccounts/groups/resequence`,
            { groupId, newIndex },
            { update: { method: 'PUT' } }
        );
        return resource.update().$promise;
    }

    function resequenceAccount(groupId, accountId, newIndex) {
        const resource = $resource(
            `${getChannelAPIPath()}dashboard/myaccounts/groups/accounts/resequence`,
            { groupId, accountId, newIndex },
            { update: { method: 'PUT' } }
        );
        return resource.update().$promise;
    }

    function getReportsOfType(reportType) {
        return $resource(`${getChannelAPIPath()}dashboard/informationreportsreports?type=:type`, {
            type: reportType,
        }).query().$promise;
    }

    function getPaymentsPendingApproval(type) {
        return $resource(`${getChannelAPIPath()}dashboard/paymentPendingApproval?type=:type`, {
            type,
        }).query().$promise;
    }

    // function getStopPaymentsPendingApproval()

    function getResources() {
        return $resource(`${getChannelAPIPath()}dashboard/resourcedetail`).query().$promise;
    }

    function getFavouriteReports() {
        return $resource(`${getChannelAPIPath()}dashboard/favouriteReports`).query().$promise;
    }

    async function getInformationCenter() {
        const promise = Promise.all([
            $resource(`${getChannelAPIPath()}dashboard/bannerAndModalMessage`).query().$promise,
            $resource(`${getChannelAPIPath()}dashboard/informationcenterdetail`).query().$promise,
        ]);
        const [bannerAndModal, informationCenter] = await $q.resolve(promise);
        const modals = bannerAndModal.filter(item => item.type === 'Modal');

        // Modals don't have sequence numbers, but it's required for the info center widget
        const maxSequence = Math.max(...informationCenter.map(item => item.sequence), 0);
        modals.forEach((item, index) => {
            item.sequence = maxSequence + index + 1;
        });

        return [...informationCenter, ...modals];
    }

    function getBannerAndModalMessages() {
        return $resource(`${getChannelAPIPath()}dashboard/bannerAndModalMessage`).query().$promise;
    }

    function getConfiguredWidgets() {
        return $resource(`${getChannelAPIPath()}dashboard/configuredWidgets`).query().$promise;
    }

    function setConfiguredWidgets(widgets) {
        const resource = $resource(`${getChannelAPIPath()}dashboard/configuredWidgets`, null, {
            update: { method: 'PUT', isArray: true },
        });
        return resource.update(widgets).$promise;
    }

    function getAvailableWidgets() {
        return $resource(`${getChannelAPIPath()}dashboard/availableWidgets`).query().$promise;
    }

    function getStopRequests() {
        return $resource(`${getChannelAPIPath()}dashboard/stopRequests`).query().$promise;
    }

    function getAvailableWidgetProducts(widgetType) {
        return $resource(`${getChannelAPIPath()}dashboard/WidgetProducts?type=:type`, {
            type: widgetType,
        }).query().$promise;
    }

    function getProductStatus() {
        return $resource(`${getChannelAPIPath()}dashboard/getProducts`).query().$promise;
    }

    function auditUserIteration(interaction) {
        const request = $resource(
            `${getChannelAPIPath()}dashboard/auditUserInteraction/${interaction}`
        );
        return request.save().$promise;
    }
}
